import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { useSelector } from "../../../common/hooks/useSelector";
import { createStyles, spacing } from "../../../styles/theme";
import { SpecsInputField } from "../SpecsEditorFields/index";
import { useSpecsEditorContextValue } from "../useSpecsEditor";
import ImageDropZone from "./ImageDropZone";
import {
  createImportProductRevision,
  prepareImportProductAssets,
  type PreparedImportAssets,
  type ProgressStep,
} from "./runImportProductCreation";
import type { ImportImageInput } from "./types";

const IMPORT_VERSION_NAME = "1.0";

const styles = createStyles({
  page: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", xl: "minmax(0, 1fr) 360px" },
    gap: spacing.groupingDesktop,
    alignItems: "start",
  },
  panel: {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: "8px",
    overflow: "hidden",
  },
  panelHeader: {
    px: 2,
    py: 1.5,
    borderBottom: "1px solid",
    borderColor: "divider",
  },
  panelBody: {
    p: 2,
  },
});

function StepIcon({ status }: { status: ProgressStep["status"] }) {
  if (status === "running") {
    return <CircularProgress size={14} />;
  }
  if (status === "done") {
    return <CheckIcon sx={{ fontSize: 14, color: "success.main" }} />;
  }
  if (status === "error") {
    return <CloseIcon sx={{ fontSize: 14, color: "error.main" }} />;
  }
  return (
    <Box
      sx={{
        width: 14,
        height: 14,
        borderRadius: "50%",
        border: "1.5px solid",
        borderColor: "divider",
      }}
    />
  );
}

function ImportProgress({ steps }: { steps: ProgressStep[] }) {
  if (steps.length === 0) {
    return null;
  }

  return (
    <Stack spacing={0.75}>
      {steps.map((step) => (
        <Stack key={step.label} direction="row" alignItems="center" spacing={1}>
          <StepIcon status={step.status} />
          <Typography
            variant="body2"
            sx={{
              color:
                step.status === "pending" ? "text.secondary" : "text.primary",
              fontWeight: step.status === "running" ? 600 : 400,
            }}
          >
            {step.label}
          </Typography>
          {step.detail ? (
            <Typography variant="caption" color="text.secondary">
              {step.detail}
            </Typography>
          ) : null}
        </Stack>
      ))}
    </Stack>
  );
}

function validateOptionalSpecsServiceUrl(value: string) {
  if (!value) {
    return undefined;
  }

  if (value !== value.trim()) {
    throw new Error(
      "Specs service URL must not include leading or trailing spaces"
    );
  }

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error("Specs service URL must be a valid URL");
  }

  if (url.protocol !== "https:" && url.protocol !== "http:") {
    throw new Error("Specs service URL must start with http:// or https://");
  }

  if (url.username || url.password) {
    throw new Error("Specs service URL must not include credentials");
  }

  if (url.pathname !== "/" || url.search || url.hash) {
    throw new Error(
      "Specs service URL must be an origin without a path, query, or hash"
    );
  }

  if (value.endsWith("/")) {
    throw new Error("Specs service URL must not end with a slash");
  }

  return value;
}

export default function SpecsImportPage() {
  const currentUser = useSelector((state) => state.userInfo) as any;
  const {
    editor: { session },
  } = useSpecsEditorContextValue();
  const [productName, setProductName] = React.useState("");
  const [displayName, setDisplayName] = React.useState("");
  const [idPrextra, setIdPrextra] = React.useState("");
  const [specsServiceUrl, setSpecsServiceUrl] = React.useState("");
  const [frontInput, setFrontInput] = React.useState<ImportImageInput | null>(
    null
  );
  const [backInput, setBackInput] = React.useState<ImportImageInput | null>(
    null
  );
  const [steps, setSteps] = React.useState<ProgressStep[]>([]);
  const [preparedAssets, setPreparedAssets] =
    React.useState<PreparedImportAssets | null>(null);
  const [createdDraft, setCreatedDraft] = React.useState<{
    productName: string;
    versionName: string;
  } | null>(null);
  const [running, setRunning] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const canRun = Boolean(
    productName.trim() &&
      idPrextra.trim() &&
      frontInput &&
      (preparedAssets || session.falSettings.apiKey)
  );

  const resetPreparedAssets = () => {
    setPreparedAssets(null);
    setCreatedDraft(null);
    setSteps([]);
  };

  const openCreatedDraft = (draft: NonNullable<typeof createdDraft>) => {
    window.location.assign(`/specs/${draft.productName}/${draft.versionName}`);
  };

  const handleRunImport = async () => {
    if (!frontInput) {
      setError("Upload a front image before importing");
      return;
    }

    setRunning(true);
    setError(null);

    try {
      const resolvedSpecsServiceUrl =
        validateOptionalSpecsServiceUrl(specsServiceUrl);
      let currentSteps = steps;
      const handleStepsChange = (nextSteps: ProgressStep[]) => {
        currentSteps = nextSteps;
        setSteps(nextSteps);
      };
      const resolvedPreparedAssets =
        preparedAssets ??
        (await prepareImportProductAssets({
          productName,
          specsServiceUrl: resolvedSpecsServiceUrl,
          frontInput,
          backInput,
          falSettings: session.falSettings,
          onStepsChange: handleStepsChange,
        }));
      setPreparedAssets(resolvedPreparedAssets);

      const result = await createImportProductRevision({
        productName,
        displayName,
        versionName: IMPORT_VERSION_NAME,
        idPrextra,
        specsServiceUrl: resolvedSpecsServiceUrl,
        preparedAssets: resolvedPreparedAssets,
        createdBy: currentUser?.email || currentUser?.id || "unknown",
        steps: currentSteps,
        onStepsChange: handleStepsChange,
      });
      toast.success(`Created import draft ${result.productName}`);
      setPreparedAssets(null);
      setCreatedDraft({
        productName: result.productName,
        versionName: result.versionName,
      });
    } catch (caughtError) {
      setSteps((current) =>
        current.map((step) =>
          step.status === "running" ? { ...step, status: "error" } : step
        )
      );
      setError(
        caughtError instanceof Error ? caughtError.message : "Import failed"
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <Typography variant="h2" sx={{ mb: 3 }}>
        Import
      </Typography>

      <Box sx={styles.page}>
        <Stack spacing={spacing.groupingDesktop}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
              gap: spacing.groupingDesktop,
            }}
          >
            <Box sx={styles.panel}>
              <Box sx={styles.panelHeader}>
                <Typography variant="subtitle2">Front</Typography>
              </Box>
              <Box sx={styles.panelBody}>
                <ImageDropZone
                  label="Front"
                  value={frontInput}
                  onChange={(input) => {
                    resetPreparedAssets();
                    setFrontInput(input);
                  }}
                />
              </Box>
            </Box>

            <Box sx={styles.panel}>
              <Box sx={styles.panelHeader}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="subtitle2">Back</Typography>
                  {backInput ? (
                    <Button
                      size="small"
                      onClick={() => {
                        resetPreparedAssets();
                        setBackInput(null);
                      }}
                    >
                      Remove
                    </Button>
                  ) : null}
                </Stack>
              </Box>
              <Box sx={styles.panelBody}>
                <ImageDropZone
                  label="Back"
                  value={backInput}
                  onChange={(input) => {
                    resetPreparedAssets();
                    setBackInput(input);
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Stack>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px" }}>
          <Stack spacing={1.5}>
            <SpecsInputField
              label="Product name"
              value={productName}
              onChange={(event) => {
                resetPreparedAssets();
                setProductName(event.target.value);
              }}
              inputProps={{ "data-test": "specsImport.productName" }}
            />
            <SpecsInputField
              label="Display name"
              value={displayName}
              onChange={(event) => {
                setCreatedDraft(null);
                setDisplayName(event.target.value);
              }}
              inputProps={{ "data-test": "specsImport.displayName" }}
            />
            <SpecsInputField
              label="ID Prextra"
              value={idPrextra}
              onChange={(event) => {
                setCreatedDraft(null);
                setIdPrextra(event.target.value);
              }}
              inputProps={{ "data-test": "specsImport.idPrextra" }}
            />
            <Button
              variant="contained"
              disabled={!canRun || running}
              onClick={handleRunImport}
              startIcon={
                running ? <CircularProgress color="inherit" size={16} /> : null
              }
              data-test="specsImport.create"
            >
              {preparedAssets ? "Retry Create Draft" : "Create Import Draft"}
            </Button>

            <ImportProgress steps={steps} />

            {error ? <Alert severity="error">{error}</Alert> : null}

            {createdDraft ? (
              <Alert severity="success">
                Created import draft{" "}
                <Link
                  component="button"
                  type="button"
                  onClick={() => openCreatedDraft(createdDraft)}
                >
                  {createdDraft.productName}
                </Link>
              </Alert>
            ) : null}

            <Box sx={{ pt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Settings
              </Typography>
              <SpecsInputField
                label="Specs service URL"
                value={specsServiceUrl}
                placeholder="Use default specs service"
                onChange={(event) => {
                  resetPreparedAssets();
                  setSpecsServiceUrl(event.target.value);
                }}
                helperText="Optional. Example: https://specs-service.staging.app.jameo.com"
                inputProps={{ "data-test": "specsImport.specsServiceUrl" }}
              />
            </Box>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}
